import { useState } from 'react';
import { isAxiosError } from 'axios';
import {
	Alert,
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	LinearProgress,
	Stack,
	Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { BasePageLayout } from '../../../components/common';
import { useSnackbar } from '../../../hooks/useSnackbar';
import type { ConversionBatch, ConversionProgress } from '../../../schemas/provinceConversion';
import { ConversionBatchTable } from './components/ConversionBatchTable';
import { ConversionMappingsTable } from './components/ConversionMappingsTable';
import { CreateBatchDialog, type CreateBatchInput } from './components/CreateBatchDialog';
import { MappingUploadPanel } from './components/MappingUploadPanel';
import { ConversionPreviewPanel } from './components/ConversionPreviewPanel';
import { ConversionPreviewMap } from './components/ConversionPreviewMap';
import {
	useApplyConversionMutation,
	useConversionBatchesQuery,
	useConversionBatchMappingsQuery,
	useCreateConversionBatchMutation,
	useCurrentBoundariesQuery,
	useDeleteConversionMutation,
	usePreviewConversionMutation,
	useRevertConversionMutation,
	useUploadMappingsMutation,
} from './hooks/useConversionBatches';

function extractErrorMessage(error: unknown, fallback: string): string {
	if (isAxiosError<{ message?: string; error?: string }>(error)) {
		return error.response?.data?.message ?? error.response?.data?.error ?? fallback;
	}
	return fallback;
}

/**
 * Trang admin cho lineage nhiều đợt (đổi mã / sáp nhập tỉnh).
 *
 * LƯU Ý PHÂN QUYỀN: `App.tsx` không có route guard theo vai trò; menu chỉ
 * ẩn/hiện theo `role`. Kiểm soát THẬT nằm ở middleware server
 * (`requireWritable`/`requireAdmin` — Phase 3). Nếu người dùng không đủ
 * quyền, các thao tác preview/apply/revert/xoá sẽ nhận 403 từ server; UI ở
 * đây không phải là hàng rào bảo mật.
 */
export function ProvinceConversionsPage() {
	const { showSuccess, showError } = useSnackbar();
	const { data: batches = [], isLoading, isError } = useConversionBatchesQuery();
	const [selectedBatch, setSelectedBatch] = useState<ConversionBatch | null>(null);
	const [createDialogOpen, setCreateDialogOpen] = useState(false);
	const [applyDialogOpen, setApplyDialogOpen] = useState(false);
	const [revertTarget, setRevertTarget] = useState<ConversionBatch | null>(null);
	const [deleteTarget, setDeleteTarget] = useState<ConversionBatch | null>(null);
	const [applyProgress, setApplyProgress] = useState<ConversionProgress | null>(null);
	const [revertProgress, setRevertProgress] = useState<ConversionProgress | null>(null);

	const createMutation = useCreateConversionBatchMutation();
	const uploadMutation = useUploadMappingsMutation();
	const previewMutation = usePreviewConversionMutation();
	const applyMutation = useApplyConversionMutation();
	const revertMutation = useRevertConversionMutation();
	const deleteMutation = useDeleteConversionMutation();
	const { data: boundaries } = useCurrentBoundariesQuery(Boolean(previewMutation.data));
	const {
		data: batchDetail,
		isLoading: isLoadingMappings,
		isError: isMappingsError,
	} = useConversionBatchMappingsQuery(selectedBatch?.id ?? null);

	const preview = previewMutation.data;

	const handleCreate = async (input: CreateBatchInput) => {
		try {
			const created = await createMutation.mutateAsync(input);
			setSelectedBatch(created);
			setCreateDialogOpen(false);
			showSuccess('Đã tạo đợt chuyển đổi (nháp).');
		} catch (error) {
			showError(extractErrorMessage(error, 'Không thể tạo đợt chuyển đổi.'));
		}
	};

	const handleUpload = async (file: File) => {
		if (!selectedBatch) return;
		try {
			const result = await uploadMutation.mutateAsync({ dotId: selectedBatch.id, file });
			showSuccess(`Đã nạp ${result.count} dòng ánh xạ.`);
			previewMutation.reset();
		} catch (error) {
			showError(extractErrorMessage(error, 'Không thể tải lên file CSV.'));
		}
	};

	const handlePreview = async () => {
		if (!selectedBatch) return;
		try {
			await previewMutation.mutateAsync(selectedBatch.id);
		} catch (error) {
			showError(extractErrorMessage(error, 'Không thể tính toán bản xem trước.'));
		}
	};

	const handleApplyConfirmed = async () => {
		if (!selectedBatch) return;
		setApplyProgress(null);
		try {
			await applyMutation.mutateAsync({ dotId: selectedBatch.id, onProgress: setApplyProgress });
			showSuccess('Đã áp dụng đợt chuyển đổi.');
			setApplyDialogOpen(false);
			previewMutation.reset();
		} catch (error) {
			showError(error instanceof Error ? error.message : 'Không thể áp dụng đợt chuyển đổi.');
		} finally {
			setApplyProgress(null);
		}
	};

	const handleRevertConfirmed = async () => {
		if (!revertTarget) return;
		setRevertProgress(null);
		try {
			await revertMutation.mutateAsync({ dotId: revertTarget.id, onProgress: setRevertProgress });
			showSuccess('Đã hoàn tác đợt chuyển đổi.');
		} catch (error) {
			showError(error instanceof Error ? error.message : 'Không thể hoàn tác đợt chuyển đổi.');
		} finally {
			setRevertTarget(null);
			setRevertProgress(null);
		}
	};

	const handleDeleteConfirmed = async () => {
		if (!deleteTarget) return;
		try {
			await deleteMutation.mutateAsync(deleteTarget.id);
			showSuccess('Đã xoá đợt chuyển đổi.');
			if (selectedBatch?.id === deleteTarget.id) setSelectedBatch(null);
		} catch (error) {
			showError(extractErrorMessage(error, 'Không thể xoá đợt chuyển đổi.'));
		} finally {
			setDeleteTarget(null);
		}
	};

	return (
		<BasePageLayout>
			<Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
				<Box>
					<Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
						Đợt chuyển đổi mã tỉnh
					</Typography>
					<Typography variant="body1" color="text.secondary">
						Tạo, xem trước và áp dụng các đợt đổi mã / sáp nhập tỉnh. Quyền áp dụng và hoàn tác do
						middleware phía máy chủ kiểm soát.
					</Typography>
				</Box>
				<Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateDialogOpen(true)}>
					Tạo đợt mới
				</Button>
			</Box>

			<ConversionBatchTable
				batches={batches}
				isLoading={isLoading}
				isError={isError}
				selectedBatchId={selectedBatch?.id ?? null}
				onSelect={(batch) => {
					setSelectedBatch(batch);
					previewMutation.reset();
				}}
				onRevert={(batch) => setRevertTarget(batch)}
				onDelete={(batch) => setDeleteTarget(batch)}
			/>

			{selectedBatch && (
				<Stack spacing={3} sx={{ mt: 3 }}>
					<Typography variant="h6">Đợt: {selectedBatch.ten}</Typography>

					{selectedBatch.trangThai !== 'DRAFT' && (
						<Alert severity="info">
							Đợt đã {selectedBatch.trangThai === 'APPLIED' ? 'được áp dụng' : 'bị hoàn tác'} — không thể sửa ánh xạ.
						</Alert>
					)}

					{selectedBatch.trangThai === 'DRAFT' && (
						<>
							<MappingUploadPanel
								onUpload={handleUpload}
								isUploading={uploadMutation.isPending}
								warnings={uploadMutation.data?.warnings ?? []}
							/>
							<Box>
								<Button variant="outlined" onClick={handlePreview} disabled={previewMutation.isPending}>
									{previewMutation.isPending ? 'Đang tính toán...' : 'Xem trước'}
								</Button>
							</Box>
							<ConversionPreviewPanel
								preview={preview}
								isLoading={previewMutation.isPending}
								isError={previewMutation.isError}
								isApplying={applyMutation.isPending}
								onApply={() => setApplyDialogOpen(true)}
							/>
							{preview?.hopLe && boundaries && (
								<ConversionPreviewMap
									dotId={selectedBatch.id}
									background={boundaries}
									bbox={preview.bbox}
									soTinhSau={preview.thongKe.soTinhSau}
								/>
							)}
						</>
					)}

					<ConversionMappingsTable
						mappings={batchDetail?.anhXas}
						isLoading={isLoadingMappings}
						isError={isMappingsError}
					/>
				</Stack>
			)}

			<CreateBatchDialog
				open={createDialogOpen}
				onClose={() => setCreateDialogOpen(false)}
				onSubmit={handleCreate}
				isSubmitting={createMutation.isPending}
			/>

			<Dialog open={applyDialogOpen} onClose={() => setApplyDialogOpen(false)}>
				<DialogTitle>Xác nhận áp dụng đợt chuyển đổi</DialogTitle>
				<DialogContent>
					<DialogContentText>
						{preview
							? `Đợt này sẽ đổi số tỉnh từ ${preview.thongKe.soTinhTruoc} xuống ${preview.thongKe.soTinhSau}. Ảnh hưởng ${preview.thongKe.soTruongAnhHuong} trường THPT.`
							: 'Vui lòng xem trước trước khi áp dụng.'}
					</DialogContentText>
					{applyMutation.isPending && (
						<Box sx={{ mt: 2 }}>
							<LinearProgress variant={applyProgress ? 'determinate' : 'indeterminate'} value={applyProgress?.percentage ?? 0} />
							<Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
								{applyProgress?.message ?? 'Đang khởi tạo...'}
							</Typography>
						</Box>
					)}
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setApplyDialogOpen(false)} disabled={applyMutation.isPending}>
						Huỷ
					</Button>
					<Button
						variant="contained"
						color="primary"
						onClick={handleApplyConfirmed}
						disabled={!preview?.hopLe || applyMutation.isPending}
					>
						{applyMutation.isPending ? 'Đang áp dụng...' : 'Áp dụng'}
					</Button>
				</DialogActions>
			</Dialog>

			<Dialog open={Boolean(revertTarget)} onClose={() => setRevertTarget(null)}>
				<DialogTitle>Xác nhận hoàn tác đợt chuyển đổi</DialogTitle>
				<DialogContent>
					<DialogContentText>
						Hoàn tác đợt &quot;{revertTarget?.ten}&quot; sẽ khôi phục ranh giới về trạng thái trước khi đợt
						này được áp dụng. Không thể hoàn tác thao tác này.
					</DialogContentText>
					{revertMutation.isPending && (
						<Box sx={{ mt: 2 }}>
							<LinearProgress variant={revertProgress ? 'determinate' : 'indeterminate'} value={revertProgress?.percentage ?? 0} />
							<Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
								{revertProgress?.message ?? 'Đang khởi tạo...'}
							</Typography>
						</Box>
					)}
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setRevertTarget(null)} disabled={revertMutation.isPending}>
						Huỷ
					</Button>
					<Button variant="contained" color="warning" onClick={handleRevertConfirmed} disabled={revertMutation.isPending}>
						{revertMutation.isPending ? 'Đang hoàn tác...' : 'Hoàn tác'}
					</Button>
				</DialogActions>
			</Dialog>

			<Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)}>
				<DialogTitle>Xoá đợt nháp</DialogTitle>
				<DialogContent>
					<DialogContentText>
						Xoá đợt &quot;{deleteTarget?.ten}&quot;? Toàn bộ ánh xạ đã tải lên của đợt này sẽ mất.
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setDeleteTarget(null)} disabled={deleteMutation.isPending}>
						Huỷ
					</Button>
					<Button variant="contained" color="error" onClick={handleDeleteConfirmed} disabled={deleteMutation.isPending}>
						Xoá
					</Button>
				</DialogActions>
			</Dialog>
		</BasePageLayout>
	);
}

export default ProvinceConversionsPage;
